<script setup lang="ts">
import { computed, defineAsyncComponent } from 'vue';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

export type LocalIconProps = {
  name: string;
  size?: 'xs' | 'small' | 'medium' | 'large' | 'xlLarge' | '32' | '36' | '40';
  class?: string;
  background?: 'grey' | 'brand' | 'success' | 'secondary' | 'destructive';
};

const props = defineProps<LocalIconProps>();

const IconComponent = computed(() =>
  defineAsyncComponent(() => import(`../assets/icons/${props.name}.svg`)),
);

const localIcon = cva('', {
  variants: {
    size: {
      xs: 'h-3.5 w-3.5',
      small: 'h-4 w-4',
      medium: 'h-5 w-5',
      large: 'h-6 w-6',
      xlLarge: 'h-12 w-12',
      '32': 'h-8 w-8',
      '36': 'h-9 w-9',
      '40': 'h-10 w-10',
    },
  },
  defaultVariants: {
    size: 'medium',
  },
});

const containerBackground = cva('inline-flex items-center justify-center shrink-0 rounded-full', {
  variants: {
    background: {
      brand: 'p-2.5 bg-blue-50',
      grey: 'p-2.5 bg-grey-50',
      success: 'p-2.5 bg-success',
      secondary: 'p-2 bg-secondary',
      destructive: 'p-2.5 bg-error-primary-hover',
    },
  },
});

const sizeClasses = computed(() => localIcon({ size: props.size }));
const containerClasses = computed(() => containerBackground({ background: props.background }));
</script>

<template>
  <span v-if="background" :class="twMerge(containerClasses, props.class)">
    <component class="fill-current" :class="sizeClasses" :is="IconComponent" />
  </span>
  <component
    v-else
    class="fill-current"
    :class="twMerge(props.class, sizeClasses)"
    :is="IconComponent"
  />
</template>

<style scoped>
.icon :deep(path),
.icon :deep(circle),
.icon :deep(rect),
.icon :deep(line) {
  fill: currentColor;
  stroke: currentColor;
}
</style>
